import { useState } from 'react';
import type { LivePosition } from '../../lib/btc15History';
import type { Btc15LiveResponse, Btc15LiveTick } from '../../lib/btc15Types';
import { formatSignedUsd, formatUsd } from '../../lib/format';
import { AnimatedUsd } from '../AnimatedUsd';
import { LiveRoundChart, type ChartMode } from './LiveRoundChart';
import styles from './Btc15LiveRound.module.css';

/**
 * A live view of the current round: BTC's path since the round opened
 * against the price to beat, a countdown, and the simulated position marked
 * to what it could be sold for right now. Read-only — nothing here places,
 * changes or closes an order; the position is a paper simulation.
 */
export function Btc15LiveRound({
  live,
  path,
  position,
  isError,
}: {
  live: Btc15LiveResponse | undefined;
  path: Btc15LiveTick[];
  position: LivePosition | null;
  isError: boolean;
}) {
  const [mode, setMode] = useState<ChartMode>('live');
  // Lives inside the round hero, under the call (which shows the countdown),
  // so it has no card or heading of its own.
  const sectionClass = styles.embedded;

  if (!live) {
    return (
      <section className={sectionClass} aria-labelledby="btc15-live-heading">
        <h2 id="btc15-live-heading" className="visually-hidden">
          Live round
        </h2>
        <p className={styles.muted}>
          {isError ? 'Live prices are unavailable right now.' : 'Loading live prices…'}
        </p>
      </section>
    );
  }

  const now = live.spot ?? path[path.length - 1]?.price ?? null;
  const target = live.target;
  const gap = now !== null && target !== null ? now - target : null;
  const upWinning = gap !== null ? gap >= 0 : null;

  // The position, marked to the bid on its own side (what selling it now
  // would fetch, before fees), and what it pays if it settles its way.
  const sideBid =
    position && live.quote
      ? position.action === 'UP'
        ? live.quote.yesBid
        : live.quote.noBid
      : null;
  const contracts = position ? position.stake / position.entryPrice : 0;
  const valueNow = position && sideBid !== null ? contracts * sideBid : null;
  const change = position && valueNow !== null ? valueNow - position.stake : null;
  const positionWinning =
    position && upWinning !== null ? (position.action === 'UP') === upWinning : null;

  return (
    <section className={sectionClass} aria-labelledby="btc15-live-heading">
      <h2 id="btc15-live-heading" className="visually-hidden">
        Live round
      </h2>

      <div className={styles.prices}>
        <div className={styles.priceBlock}>
          <span className={styles.label}>BTC now</span>
          <AnimatedUsd value={now} className={styles.priceNow} />
          {gap !== null && (
            <span className={styles.gap}>
              {formatUsd(Math.abs(gap))} {gap >= 0 ? 'above' : 'below'} the target
            </span>
          )}
        </div>
        <div className={`${styles.priceBlock} ${styles.priceBlockEnd}`}>
          <span className={styles.label}>Price to beat</span>
          <span className={styles.priceTarget}>{formatUsd(target)}</span>
          <span className={styles.gap}>
            {live.targetSource === 'kalshi' ? 'Kalshi strike' : 'Round open (stand-in)'}
          </span>
        </div>
      </div>

      {path.length > 0 && (
        <>
          <div className={styles.modesRow}>
            <div className={styles.modes} role="group" aria-label="Chart range">
              <button
                type="button"
                className={styles.modeButton}
                aria-pressed={mode === 'live'}
                onClick={() => setMode('live')}
              >
                Last 2 min
              </button>
              <button
                type="button"
                className={styles.modeButton}
                aria-pressed={mode === 'round'}
                onClick={() => setMode('round')}
              >
                Whole round
              </button>
            </div>
            <span className={styles.liveTag}>
              <span className={styles.liveDot} aria-hidden="true" />
              Live
            </span>
          </div>
          <LiveRoundChart
            path={path}
            target={target}
            startMs={live.round.startMs}
            closeMs={live.round.closeMs}
            mode={mode}
            summary={
              now !== null && target !== null
                ? `BTC is ${formatUsd(now)}, ${formatUsd(Math.abs(now - target))} ${now >= target ? 'above' : 'below'} the ${formatUsd(target)} price to beat.`
                : 'Live price unavailable.'
            }
          />
        </>
      )}

      <p className={styles.rule}>
        UP wins if BTC settles at or above {formatUsd(target)}; DOWN wins below it. Kalshi
        settles on a 60-second average of the CF Benchmarks index at the close;
        Coinbase&apos;s price is shown as a stand-in.
      </p>

      {position ? (
        <div className={styles.position} aria-label="Simulated position">
          <div className={styles.positionHead}>
            <span className={styles.positionSide}>
              {position.action === 'UP' ? '↗' : '↘'} {position.action}
            </span>
            <span className={styles.badge}>Simulated, no real order</span>
            {positionWinning !== null && (
              <span className={styles.positionState}>
                {positionWinning
                  ? 'Would pay out if it ended now'
                  : 'Would not pay out if it ended now'}
              </span>
            )}
          </div>
          <dl className={styles.stats}>
            <div>
              <dt>Simulated buy</dt>
              <dd>{formatUsd(position.stake)}</dd>
            </div>
            <div>
              <dt>Entry</dt>
              <dd>{(position.entryPrice * 100).toFixed(1)}¢</dd>
            </div>
            <div>
              <dt>Worth if sold now</dt>
              <dd>
                {valueNow === null ? '—' : formatUsd(valueNow)}
                {change !== null && (
                  <span className={styles.change}> {formatSignedUsd(change)}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Pays if right</dt>
              <dd>{formatUsd(contracts)}</dd>
            </div>
          </dl>
          <p className={styles.fine}>
            {contracts.toFixed(1)} contracts. &ldquo;Worth if sold now&rdquo; uses the
            live bid on this side, before fees.
          </p>
        </div>
      ) : (
        <p className={styles.muted}>No simulated buy this round.</p>
      )}
    </section>
  );
}
